/**
 * attemptQueue.js - getting every attempt to the server, without ever making
 * the child wait for it.
 *
 * PLATFORM SCAFFOLD. HTTP, retries, and never making a child wait. Nothing in
 * it is about children or about any one game.
 *
 * PURE-ish: the network call, the clock and the storage are all injected, so
 * the retry rules are tested without a server.
 *
 * ---------------------------------------------------------------------------
 * WHY A QUEUE AND NOT AN AWAIT
 *
 * Section 8.6: log everything. The engine's chart is built from attempts, so a
 * dropped attempt is a hole in a child's record that nothing later can fill.
 * But a POST is on the far side of a mobile connection, and a child mid-flow
 * must never see a spinner between saying "apple" and the bubble popping. So
 * the game posts into this queue and carries on; the queue drains behind it.
 *
 * The queue is also what keeps a session playable when the server cannot be
 * reached at all: the attempts sit in it and drain when the connection comes
 * back. That is the behaviour needed on a train, in a clinic basement, and
 * during a deploy - and it is why a 503 is retried forever rather than
 * surfaced. Nothing about it is visible to the child.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS WORTH RETRYING
 *
 *   503 / 502 / 504 / network  the server is not ready or not reachable. Retry
 *                              with backoff, forever, until the session ends.
 *   429                        slow down; retry.
 *   400                        the game sent the wrong component set for this
 *                              level. Retrying cannot fix it, and it is a BUG:
 *                              surface it loudly to the grown-ups panel and
 *                              drop the item. Section 8.1 rule 3 refusals are
 *                              the engine telling the game it is wrong.
 *   401 / 403 / 404            the session or the child is gone. Stop the
 *                              queue; nothing further will succeed.
 */

/** HTTP statuses that mean "try again later". */
export const RETRYABLE = new Set([0, 408, 425, 429, 500, 502, 503, 504]);

/** Statuses that mean the game is wrong and must be told. */
export const FATAL_TO_ITEM = new Set([400, 409, 422]);

/** Statuses that mean stop trying entirely. */
export const FATAL_TO_QUEUE = new Set([401, 403, 404]);

export const DEFAULT_BACKOFF_MS = [1000, 3000, 8000, 20000, 45000];

/**
 * @param {object} input
 * @param {(payload:object) => Promise<object>} input.post   throws with .status
 * @param {() => number} [input.now]                          ms clock
 * @param {(state:object) => void} [input.onChange]
 * @param {(item:object, error:Error) => void} [input.onRejected] a 400: a bug
 * @param {number[]} [input.backoffMs]
 * @returns {object} queue
 */
export function createAttemptQueue({
  post,
  now = () => Date.now(),
  onChange = () => {},
  onRejected = () => {},
  backoffMs = DEFAULT_BACKOFF_MS,
} = {}) {
  const pending = [];
  const rejected = [];
  let sent = 0;
  let stopped = false;
  let stoppedReason = null;
  let draining = false;
  let nextId = 1;
  let lastError = null;

  const snapshot = () => ({
    pending: pending.length,
    sent,
    rejected: rejected.length,
    stopped,
    stoppedReason,
    lastError,
    nextAttemptAt: pending.length ? pending[0].nextAt : null,
  });

  const changed = () => onChange(snapshot());

  return {
    /**
     * @param {object} payload the POST /api/engine/attempts body
     * @returns {number} item id
     */
    enqueue(payload) {
      const id = nextId;
      nextId += 1;
      pending.push({ id, payload, tries: 0, nextAt: now(), queuedAt: now() });
      changed();
      return id;
    },

    /**
     * Send what is due. Safe to call on a timer and safe to call re-entrantly:
     * a second call while one is in flight returns immediately rather than
     * sending the same attempt twice.
     *
     * @returns {Promise<{sent:number, deferred:number}>}
     */
    async drain() {
      if (draining || stopped) return { sent: 0, deferred: pending.length };
      draining = true;
      let delivered = 0;

      try {
        while (pending.length && !stopped) {
          const item = pending[0];
          if (item.nextAt > now()) break;

          try {
            const result = await post(item.payload);
            pending.shift();
            sent += 1;
            delivered += 1;
            lastError = null;
            item.result = result;
            changed();
          } catch (error) {
            const status = Number(error && error.status) || 0;
            lastError = { status, message: error && error.message, at: now() };

            if (FATAL_TO_QUEUE.has(status)) {
              stopped = true;
              stoppedReason = error && error.message;
              changed();
              break;
            }

            if (FATAL_TO_ITEM.has(status)) {
              // The engine refused the shape of this attempt. That is a coding
              // error in the game, not a transport problem; keep it where a
              // developer will see it and move on rather than blocking the
              // rest of the session behind it.
              pending.shift();
              rejected.push({ item, error: { status, message: error && error.message, details: error && error.details } });
              onRejected(item, error);
              changed();
              continue;
            }

            if (!RETRYABLE.has(status)) {
              // Unknown failure: treat as retryable rather than losing data.
              lastError.unknown = true;
            }

            item.tries += 1;
            item.nextAt = now() + backoffMs[Math.min(item.tries - 1, backoffMs.length - 1)];
            changed();
            break;
          }
        }
      } finally {
        draining = false;
      }

      return { sent: delivered, deferred: pending.length };
    },

    /** Everything still unsent, for handing to storage on unload. */
    drainable() { return pending.map((i) => i.payload); },

    /** Attempts the engine refused. Each one is a bug worth fixing. */
    rejections() { return rejected.slice(); },

    state() { return snapshot(); },

    /** After a fatal stop, once the caller believes the cause is fixed. */
    resume() {
      stopped = false;
      stoppedReason = null;
      changed();
    },
  };
}

export default { createAttemptQueue, RETRYABLE, FATAL_TO_ITEM, FATAL_TO_QUEUE };
